/** «EXPLOREAZĂ PIAȚA» pentru o resursă: ce prețuri s-au văzut pe piață pentru
 *  ea, din ce surse, pe ce bază, și care e cel pe care l-ar lua proiectul.
 *
 *  Prețurile vin din colecția globală a mediului, aduse de server la forma
 *  catalogului — lei fără TVA pe unitatea resursei, franco șantier, fără pompă
 *  — și trecute în EUR cu cursul proiectului. Pe fiecare rând se vede și cum a
 *  ajuns acolo: prețul cum l-a arătat sursa (cu sau fără TVA, pe ce unitate) și
 *  ajustarea făcută (transportul adăugat, pompa scăzută), fiindcă ajustarea e
 *  o ipoteză a colectorului și omul trebuie s-o poată judeca.
 *
 *  «Cel mai mic» e cel mai mic dintre prețurile potrivite EXACT pe
 *  specificație. Un echivalent — altă clasă de expunere, o marcă veche de oțel
 *  — se arată la cerere, dar nu se poate folosi: un preț mic pentru alt produs
 *  ar strica tăcut costul.
 *
 *  «Folosește» scrie prețul pe toate țintele cheii, în copia PROIECTULUI (nu
 *  într-un scenariu): ofertele vin apoi ca scenarii peste el. «Scoate prețul
 *  pieței» întoarce țintele la prețul de dinaintea importului. */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ApiError } from "../../api";
import SeIncarca from "../../components/SeIncarca";
import { data, numar, t, tn, useT } from "../../i18n";
import { pretUnitar, simbolMoneda } from "../../structure/bani";
import { Lamurire } from "./Termeni";
import {
  aplicaPeCheie, folosibil, getCheiePiata, importaPiata, randuriVazute, textBaza, textVarianta, umPiata,
  type Aplica, type DetaliuCheie, type RandPiata,
} from "./piataResurse";
import "./piata.css";
import "./piataResurse.css";

const DOCUMENTE = new Set(["fisa_tehnica", "dop", "agrement"]);
const numeDocument = (fel: string) =>
  DOCUMENTE.has(fel) ? t(`piata_res.doc.${fel as "fisa_tehnica" | "dop" | "agrement"}`) : t("piata_res.doc.alt");

/** Moneda sursei, cum o scrie omul: «lei», nu «RON». */
const moneda = (m: string) => (/^(RON|LEI)$/i.test(m) ? t("struct.bani.lei") : m);

/** Prețul cum l-a arătat sursa: «482,00 lei/metru cub», fără nicio conversie. */
const pretVazut = (r: RandPiata) =>
  `${numar(r.pret, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${moneda(r.moneda)}`
  + (r.um_sursa ? `/${r.um_sursa}` : "");

/** Sertarul se închide cu Escape, ca celelalte sertare ale aplicației. */
function useEscape(laInchide: () => void) {
  useEffect(() => {
    const tasta = (e: KeyboardEvent) => { if (e.key === "Escape") laInchide(); };
    window.addEventListener("keydown", tasta);
    return () => window.removeEventListener("keydown", tasta);
  }, [laInchide]);
}

/** Sertarul din dreapta, în rădăcina aplicației (`.ob`), ca al vânzărilor din
 *  zonă (`PiataPeBrief.tsx`): dinăuntrul tabelului ar rămâne sub bara de sus. */
export function Sertar({ titlu, laInchide, children }: {
  titlu: string; laInchide: () => void; children: ReactNode;
}) {
  useEscape(laInchide);
  return createPortal(
    <div className="cp-sertar-fundal" onClick={laInchide}>
      <div className="cp-sertar pr-sertar obs" role="dialog" aria-modal="true" aria-labelledby="pr-sertar-h"
           onClick={(e) => e.stopPropagation()}>
        <div className="cp-sertar-cap">
          <b id="pr-sertar-h">{titlu}</b>
          <span className="cp-sp" />
          <button type="button" className="btn btng" onClick={laInchide} aria-label={t("piata_res.inchide")}>✕</button>
        </div>
        <div className="cp-sertar-corp">{children}</div>
      </div>
    </div>,
    document.querySelector(".ob") ?? document.body,
  );
}

export default function ExploreazaPiata({ projectId, solutieId, cheie, potScrie, motivBlocat = null,
                                          colectatLa = null, laInchide, laSchimbat }: {
  projectId: string;
  /** soluția listei de cantități: țintele și prețurile de acum sunt ale ei */
  solutieId: string;
  cheie: string;
  potScrie: boolean;
  /** de ce nu se poate scrie acum, deși omul are drept (schimbări nesalvate pe pagină) */
  motivBlocat?: string | null;
  /** ultima colectare a mediului (din rezumatul pieței, citit de părinte) */
  colectatLa?: string | null;
  laInchide: () => void;
  /** după un import sau o scoatere: părintele reface catalogul și lista */
  laSchimbat: () => void;
}) {
  useT();
  const [date, setDate] = useState<DetaliuCheie | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [mesaj, setMesaj] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [doarExacte, setDoarExacte] = useState(true);

  const incarca = useCallback(async () => {
    try {
      setDate(await getCheiePiata(projectId, solutieId, cheie));
      setEroare(null);
    } catch (e) {
      setEroare(t("piata_res.nu_s_a_citit", { e: (e as Error).message }));
    }
  }, [projectId, solutieId, cheie]);
  useEffect(() => { void incarca(); }, [incarca]);

  const k = date?.cheie ?? null;
  const um = umPiata(k?.um);
  const titlu = t("piata_res.titlu", { nume: k?.nume ?? cheie, um });
  if (!date) {
    return (
      <Sertar titlu={titlu} laInchide={laInchide}>
        {eroare ? <div className="sg-error">{eroare}</div> : <SeIncarca />}
      </Sertar>
    );
  }

  const tinte = date.tinte;
  const rez = k?.rezumat ?? null;
  const randuri = randuriVazute(date.randuri, doarExacte);
  const nEchivalente = date.randuri.filter((r) => r.echivalent).length;
  const dinPiataIds = tinte.filter((x) => x.din_piata).map((x) => x.id);
  const fataCurs = date.curs == null;
  const poate = potScrie && !motivBlocat && !busy && tinte.length > 0 && !fataCurs;
  const numeTinta = (id: string) => tinte.find((x) => x.id === id)?.nume ?? id;

  async function trimite(aplica: Aplica[], revino: string[] = []) {
    setBusy(true);
    setMesaj(null);
    setEroare(null);
    try {
      const r = await importaPiata(projectId, solutieId, aplica, revino);
      const parti: string[] = [];
      if (aplica.length) parti.push(tn("piata_res.pus#n", r.aplicate.length));
      if (revino.length) parti.push(tn("piata_res.scos#n", revino.filter((id) => !r.respinse.some((x) => x.tinta === id)).length));
      if (r.respinse.length) {
        parti.push(t("piata_res.respinse", { l: r.respinse.map((x) => `${numeTinta(x.tinta)} (${x.motiv})`).join("; ") }));
      }
      setMesaj(parti.join(" "));
      laSchimbat();
      await incarca();
    } catch (e) {
      setEroare(e instanceof ApiError && e.status === 409 ? t("lc.catalog.calcat") : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sertar titlu={titlu} laInchide={laInchide}>
      <div className="pr-cap">
        <div>
          <div className="pr-eticheta">{t("piata_res.in_proiect")}</div>
          {tinte.length === 0 ? <div className="sg-muted">{t("piata_res.fara_tinte")}</div> : (
            <ul className="pr-tinte">
              {tinte.map((x) => (
                <li key={x.id}>
                  <span>{x.nume}</span>{" "}
                  <b className="sg-mono">{pretUnitar(x.pret_eur, umPiata(x.um))}</b>
                  {x.din_piata && <span className="lc-fel lc-fel-piata">{t("piata_res.din_piata")}</span>}
                  {x.pus_de_om && <span className="lc-fel lc-fel-om">{t("piata_res.pus_de_om")}</span>}
                  {x.sursa && <span className="sg-muted"> · {x.sursa}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="pr-rezumat">
          {rez ? (
            <>
              {t("piata_res.rezumat", {
                min: pretUnitar(rez.minim.eur, um), med: pretUnitar(rez.mediana_eur, um), max: pretUnitar(rez.maxim_eur, um),
                preturi: tn("piata_res.preturi#n", rez.n), surse: tn("piata_res.surse#n", rez.n_surse),
              })}
              <div className="sg-muted">
                {t("piata_res.cel_mai_mic")}: {rez.minim.sursa_nume} · {rez.minim.nume} · {t("piata_res.vazut_la", { la: data(rez.minim.vazut_la) })}
                {colectatLa && <> · {t("piata_res.colectat", { la: data(colectatLa) })}</>}
              </div>
            </>
          ) : <span className="sg-muted">{t("piata_res.fara_preturi")}</span>}
        </div>
        <div className="pr-actiuni">
          {rez && (
            <button type="button" className="btn btnp" disabled={!poate} title={motivBlocat ?? undefined}
                    onClick={() => void trimite(aplicaPeCheie(tinte, cheie))}>
              {busy ? t("piata_res.lucrez") : t("piata_res.foloseste_minim", { p: pretUnitar(rez.minim.eur, um) })}
            </button>
          )}
          {dinPiataIds.length > 0 && (
            <button type="button" className="btn" disabled={!potScrie || !!motivBlocat || busy}
                    title={motivBlocat ?? t("piata_res.scoate_t")} onClick={() => void trimite([], dinPiataIds)}>
              {t("piata_res.scoate")}
            </button>
          )}
          <label className="lc-inline" title={t("piata_res.doar_exacte_t")}>
            <input type="checkbox" checked={doarExacte} onChange={(e) => setDoarExacte(e.target.checked)} />
            <span>{t("piata_res.doar_exacte")}{nEchivalente > 0 && <span className="sg-muted"> ({nEchivalente})</span>}</span>
          </label>
          <Lamurire text={t("piata_res.pe_proiect")} />
        </div>
        {motivBlocat && potScrie && <div className="sg-muted">{motivBlocat}</div>}
        {fataCurs && tinte.length > 0 && <div className="sg-muted">{t("piata_res.fara_curs")}</div>}
        {mesaj && <div className="pr-mesaj" role="status">{mesaj}</div>}
        {eroare && <div className="sg-error">{eroare}</div>}
      </div>

      {randuri.length === 0 ? (
        <div className="sg-muted">{t("piata_res.fara_preturi")}</div>
      ) : (
        <div className="bc-scroll">
          <table className="sg-table lc-tabel pr-tabel">
            <thead>
              <tr>
                <th>{t("piata_res.th.sursa")}</th>
                <th>{t("piata_res.th.produs")}</th>
                <th>{t("piata_res.th.vazut")}</th>
                <th className="bc-n">{t("piata_res.th.pe_baza", { m: simbolMoneda(), um })}</th>
                <th>{t("piata_res.th.stare")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {randuri.map((r, i) => {
                const baza = textBaza(r, k?.um ?? "");
                const varianta = textVarianta(r.varianta);
                const eMinim = !!rez && folosibil(r) && r.produs_id === rez.minim.produs_id && r.vazut_la === rez.minim.vazut_la;
                const cls = r.echivalent ? "pr-echivalent" : eMinim ? "pr-minim" : undefined;
                return (
                  <tr key={`${r.produs_id}|${r.vazut_la}|${r.locatie}|${i}`} className={cls}>
                    <td>
                      <span className={`pr-nivel pr-nivel-${r.nivel}`} title={t(`piata_res.nivel.${r.nivel}`)}>●{r.nivel}</span>
                      {r.sursa_nume}
                    </td>
                    <td>
                      {r.url ? <a href={r.url} target="_blank" rel="noreferrer noopener">{r.nume} ↗</a> : r.nume}
                      {varianta && <div className="sg-muted">{varianta}</div>}
                      {baza && <div className="sg-muted" title={r.ajustare ? t("piata_res.ajustare_t") : undefined}>{baza}</div>}
                      {r.furnizor && <div className="sg-muted">{t("piata_res.furnizor", { f: r.furnizor })}</div>}
                      {r.autoritate && <div className="sg-muted">{t("piata_res.autoritate", { a: r.autoritate })}</div>}
                      {r.documente.length > 0 && (
                        <div className="pr-docs">
                          {r.documente.map((d, j) => (
                            <a key={j} href={d.url} target="_blank" rel="noreferrer noopener">{numeDocument(d.fel)} ↗</a>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="sg-mono">
                      {pretVazut(r)} <span className="sg-muted">{r.cu_tva ? t("piata_res.cu_tva") : t("piata_res.fara_tva")}</span>
                      {r.cantitate != null && <div className="sg-muted">× {numar(r.cantitate)}{r.um_sursa ? ` ${r.um_sursa}` : ""}</div>}
                      <div className="sg-muted">{data(r.vazut_la)}{r.locatie ? ` · ${r.locatie}` : ""}</div>
                    </td>
                    <td className="sg-mono bc-n">
                      {r.echivalent ? <span title={r.motiv_echivalent ?? undefined}>{t("piata_res.echivalent")}</span>
                        : r.pe_baza_eur == null ? <span className="sg-muted">{t("piata_res.fara_baza")}</span>
                        : pretUnitar(r.pe_baza_eur)}
                      {eMinim && <div><span className="lc-fel lc-fel-piata">{t("piata_res.cel_mai_mic")}</span></div>}
                    </td>
                    <td>
                      {r.stare !== "ok" && (
                        <span className="lc-fel lc-fel-lipsa" title={r.motiv ?? undefined}>{t(`piata_res.stare.${r.stare}`)}</span>
                      )}
                    </td>
                    <td>
                      {folosibil(r) && (
                        <button type="button" className="btn lc-x" disabled={!poate}
                                title={motivBlocat ?? t("piata_res.foloseste_t")}
                                onClick={() => void trimite(aplicaPeCheie(tinte, cheie, r.produs_id))}>
                          {t("piata_res.foloseste")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Sertar>
  );
}
